import React from "react";
import Reveal from "./Reveal";
import { Sparkles, Handshake } from "lucide-react";

const featured = {
  name: "50 / 50 Split",
  tag: "Incubator · Partnership",
  stage: "Actively seeking partners",
  headline: "Bring your idea. I'll bring mine. We split 50/50.",
  copy:
    "I've always wanted a partnership vehicle where it's honest from day one. You bring an idea and some money. I bring ideas and some money. Worst case — we split it 50/50. Best case — we build a company together.",
  deal: "50 / 50 equity · shared capital · co-built",
};

const ideas = [
  {
    name: "BackYa",
    tag: "Investment Vehicle",
    stage: "Concept",
    copy:
      "Like 50/50 Split, but I back you. A backer's stake for founders who've got the idea handled and need fuel.",
  },
  {
    name: "Wealth Management",
    tag: "Legacy Finance",
    stage: "Family legacy",
    copy:
      "The firm that will manage my wealth — and eventually my son's. A generational legacy vehicle in the making.",
  },
  {
    name: "Ambrosia Water",
    tag: "Consumer · Beverage",
    stage: "Scouting a source",
    copy:
      "High-pH natural spring water. I want to find the source, buy it, and bottle the best water in America.",
  },
  {
    name: "What Smells?",
    tag: "Fragrance · Deodorizing",
    stage: "Concept",
    copy:
      "A deodorizing and fragrance company. I've always wanted to make things smell good — this is that company.",
  },
  {
    name: "Lights Camera Internet",
    tag: "Online Media",
    stage: "Concept",
    copy:
      "An online media company for the kinds of stories, shows and shorts that don't get made elsewhere.",
  },
  {
    name: "Strategy Mill",
    tag: "Incubator · Think Tank",
    stage: "Concept",
    copy:
      "Where we strategize about business — and incubate the ones worth building. A mill for ideas.",
  },
  {
    name: "Creators University",
    tag: "Education",
    stage: "Concept",
    copy:
      "A university for builders. Creators create value — Creators University is where the next wave learns how.",
  },
  {
    name: "GPS PPC",
    tag: "AdTech",
    stage: "Concept",
    copy:
      "Draw a circle on a map. Run ads only inside it. Hyper-local paid advertising at precision scale.",
  },
  {
    name: "QR Mall",
    tag: "Retail · Commerce",
    stage: "Concept",
    copy:
      "A mall in every subway and every hallway in America. Scan a poster, buy the product — one click, anywhere.",
  },
  {
    name: "Anofeed",
    tag: "Social · App",
    stage: "Concept",
    copy:
      "Constructive criticism from friends and family — anonymously. Grow from real feedback without knowing who gave it.",
  },
];

export const FutureIdeas = () => {
  return (
    <section
      id="future"
      data-testid="future-ideas-section"
      className="relative py-28 lg:py-40 border-t border-gold-deep/40 z-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-24">
            <div>
              <div className="overline mb-6 flex items-center gap-3">
                <Sparkles className="w-3.5 h-3.5" />
                Future · In Progress
              </div>
              <h2 className="heading-section text-5xl lg:text-7xl text-white max-w-3xl">
                Eleven <span className="italic gold-gradient-text">ideas</span> looking for the
                right partner.
              </h2>
            </div>
            <p className="body-copy max-w-md text-base lg:text-right">
              Working concepts, half-built playbooks, and legacy plays. If one of these feels like
              yours — pitch me. Fifty-fifty's on the table.
            </p>
          </div>
        </Reveal>

        {/* Featured: 50/50 Split */}
        <Reveal>
          <div
            className="relative overflow-hidden mb-14 border border-gold/50 bg-gradient-to-br from-[#14110a] via-[#0a0906] to-[#060606] p-10 lg:p-16 group"
            data-testid="future-featured-5050-split"
          >
            <div className="absolute -top-3 -left-3 w-10 h-10 border-t border-l border-gold" />
            <div className="absolute -bottom-3 -right-3 w-10 h-10 border-b border-r border-gold" />

            {/* Halo */}
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_60%)] pointer-events-none" />

            <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-gold bg-gold/10 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                    {featured.stage}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                    {featured.tag}
                  </span>
                </div>

                <h3 className="heading-section text-5xl lg:text-7xl text-white mb-6 leading-[0.95]">
                  <span className="italic gold-gradient-text">{featured.name}</span>
                </h3>

                <p className="font-serif text-white/90 text-2xl lg:text-3xl leading-snug mb-6">
                  {featured.headline}
                </p>

                <p className="body-copy text-base lg:text-lg max-w-xl mb-8">{featured.copy}</p>

                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href="#book"
                    className="btn-primary"
                    data-testid="future-featured-cta-book"
                  >
                    <Handshake className="w-4 h-4" />
                    Pitch 50/50
                  </a>
                  <a
                    href="#contact"
                    className="btn-secondary"
                    data-testid="future-featured-cta-contact"
                  >
                    Send Your Idea
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-5">
                <div className="border border-gold-deep/70 p-6 bg-black/40">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80 mb-3">
                    Deal structure
                  </div>
                  <div className="font-serif text-white text-xl leading-snug">{featured.deal}</div>
                </div>
                <div className="border border-gold-deep/70 p-6 bg-black/40">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80 mb-3">
                    Who it's for
                  </div>
                  <ul className="space-y-2 font-serif text-white/90 text-base">
                    <li>— Founders with more conviction than capital</li>
                    <li>— Operators who want a working partner, not a passive check</li>
                    <li>— Ideas that deserve to ship</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* The other 10 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 border border-gold-deep/50">
          {ideas.map((idea, idx) => (
            <Reveal
              key={idea.name}
              delay={(idx % 5) * 60}
              className="group relative border border-gold-deep/30 -m-px p-7 lg:p-8 bg-[#060606] hover:bg-[#0d0a04] transition-all duration-500 cursor-default flex flex-col"
              data-testid={`future-idea-${idea.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">
                  {String(idx + 2).padStart(2, "0")}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 text-right max-w-[120px]">
                  {idea.tag}
                </span>
              </div>

              <h3 className="heading-section text-2xl lg:text-3xl text-white mb-3 group-hover:text-gold transition-colors duration-500">
                {idea.name}
              </h3>

              <p className="body-copy text-sm text-white/60 flex-1">{idea.copy}</p>

              <div className="mt-6 pt-5 border-t border-gold-deep/50 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">
                  {idea.stage}
                </span>
                <span className="h-px w-6 bg-gold-deep group-hover:w-16 group-hover:bg-gold transition-all duration-700" />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200}>
          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-8 lg:p-10 border border-gold-deep/60 bg-gradient-to-br from-[#0c0a04] to-[#060606]">
            <div>
              <div className="font-serif text-3xl lg:text-4xl text-white">
                See one you want to build?{" "}
                <span className="italic gold-gradient-text">Let's talk structure.</span>
              </div>
              <p className="body-copy text-sm mt-2 max-w-xl">
                50/50, investor-backed, advisor-for-equity, or something custom. If the idea is
                right, the deal follows.
              </p>
            </div>
            <a href="#book" className="btn-primary shrink-0" data-testid="future-cta">
              Pitch Ryan
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default FutureIdeas;
